import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Save, Upload, Image as ImageIcon, Trash2, Eye, Settings } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSiteSettingsPage = () => {
  const { t, tError } = useLanguage();
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/public/site-settings`);
      setSettings(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const update = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/api/admin/site-settings`, settings, { headers });
      setMsg('Sauvegarde !');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Erreur: ' + (tError(e.response?.data?.detail) || e.message)); }
    finally { setSaving(false); }
  };

  const uploadImage = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/admin/site-settings/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      if (target === 'background') {
        update('background_image', res.data.url);
      } else if (target === 'gallery') {
        const gallery = [...(settings.gallery_images || []), res.data.url];
        update('gallery_images', gallery);
      }
    } catch (e) { alert('Erreur upload: ' + e.message); }
    finally { setUploading(false); }
  };

  const removeGalleryImage = (idx) => {
    const gallery = [...(settings.gallery_images || [])];
    gallery.splice(idx, 1);
    update('gallery_images', gallery);
  };

  const Field = ({ label, field, type = 'text', rows }) => (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      {rows ? (
        <textarea value={settings[field] || ''} onChange={e => update(field, e.target.value)}
          rows={rows} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none" />
      ) : (
        <input type={type} value={settings[field] || ''} onChange={e => update(field, e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none" />
      )}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-site-settings">
        <PageHeader
          title={t('adminSettings.title')}
          subtitle="Contenu public du site (landing, SEO, contact)"
          backTo="/dashboard"
          icon={Settings}
          testId="admin-site-settings-header"
          actions={
            <>
              <a href="/" target="_blank" rel="noreferrer"
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white text-xs flex items-center gap-1">
                <Eye className="w-3 h-3" /> {t('adminSettings.preview')}
              </a>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                data-testid="save-settings-btn">
                <Save className="w-3 h-3" /> {saving ? t('adminSettings.saving') : t('adminSettings.save')}
              </button>
            </>
          }
        />
      {msg && <div className={`text-sm p-2 rounded-lg ${msg.startsWith('Erreur') || msg.startsWith('Error') ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>{msg}</div>}

      {/* Hero section */}
      <div className="bg-white/[.03] border border-white/10 rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('adminSettings.heroSection')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('adminSettings.mainTitle')} field="hero_title" />
          <Field label={t('adminSettings.subtitle')} field="hero_subtitle" />
          <Field label={t('adminSettings.accentColor')} field="accent_color" />
        </div>

        {/* Background image */}
        <div>
          <label className="block text-xs text-gray-400 mb-2">{t('adminSettings.bgImage')}</label>
          <div className="flex items-center gap-3">
            {settings.background_image ? (
              <div className="relative w-40 h-24 rounded-lg overflow-hidden border border-white/10">
                <img src={`${API}${settings.background_image}`} alt="" className="w-full h-full object-cover" />
                <button onClick={() => update('background_image', '')}
                  className="absolute top-1 right-1 p-1 bg-red-600 rounded-full">
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ) : (
              <div className="w-40 h-24 rounded-lg border-2 border-dashed border-white/10 flex items-center justify-center text-gray-600">
                <ImageIcon className="w-6 h-6" />
              </div>
            )}
            <label className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white text-xs cursor-pointer flex items-center gap-1">
              <Upload className="w-3 h-3" /> {uploading ? '...' : t('adminSettings.change')}
              <input type="file" accept="image/*" className="hidden" onChange={e => uploadImage(e, 'background')} />
            </label>
          </div>
        </div>
      </div>

      {/* Company Info */}
      <div className="bg-white/[.03] border border-white/10 rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('adminSettings.companyInfo')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('adminSettings.companyName')} field="company_name" />
          <Field label={t('adminSettings.companyType')} field="company_type" />
          <Field label={t('adminSettings.capital')} field="capital" />
          <Field label={t('adminSettings.rcs')} field="rcs" />
          <Field label={t('adminSettings.siret')} field="siret" />
          <Field label={t('adminSettings.euid')} field="euid" />
          <Field label={t('adminSettings.headquarters')} field="siege_social" />
          <Field label={t('adminSettings.salesPoint')} field="point_de_vente" />
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white/[.03] border border-white/10 rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('adminSettings.contactHours')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('adminSettings.phone')} field="telephone" />
          <Field label={t('adminSettings.whatsapp')} field="whatsapp" />
          <Field label={t('adminSettings.email')} field="email" />
          <Field label={t('adminSettings.googleMaps')} field="google_maps_query" />
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-white/[.03] border border-white/10 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('adminSettings.gallery')}</h2>
          <label className="px-3 py-2 rounded-lg bg-violet-500/20 text-violet-400 border border-violet-500/30 text-xs cursor-pointer flex items-center gap-1 hover:bg-violet-500/30">
            <Upload className="w-3 h-3" /> {t('adminSettings.addImage')}
            <input type="file" accept="image/*" className="hidden" onChange={e => uploadImage(e, 'gallery')} />
          </label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(settings.gallery_images || []).map((img, i) => (
            <div key={i} className="relative rounded-lg overflow-hidden border border-white/10 aspect-video">
              <img src={`${API}${img}`} alt="" className="w-full h-full object-cover" />
              <button onClick={() => removeGalleryImage(i)}
                className="absolute top-1 right-1 p-1 bg-red-600 rounded-full hover:bg-red-700">
                <Trash2 className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Ad Spaces */}
      <div className="bg-white/[.03] border border-white/10 rounded-2xl p-5 space-y-4" data-testid="ad-spaces-section">
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Espaces publicitaires</h2>
        <p className="text-xs text-gray-500">Configurez les espaces publicitaires gauche et droit pour afficher des annonces tierces sur le site.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-gray-300">Sidebar gauche</h3>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Titre de l'annonce</label>
              <input type="text" value={settings.ad_left_title || ''} onChange={e => update('ad_left_title', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none" placeholder="Ex: Sponsor principal" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Lien (URL)</label>
              <input type="text" value={settings.ad_left_link || ''} onChange={e => update('ad_left_link', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Code HTML / Script (optionnel)</label>
              <textarea value={settings.ad_left_html || ''} onChange={e => update('ad_left_html', e.target.value)}
                rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none font-mono" placeholder="<script>...</script> ou <iframe>...</iframe>" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Image publicitaire</label>
              <div className="flex items-center gap-3">
                {settings.ad_left_image ? (
                  <div className="relative w-32 h-20 rounded-lg overflow-hidden border border-white/10">
                    <img src={`${API}${settings.ad_left_image}`} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => update('ad_left_image', '')} className="absolute top-1 right-1 p-0.5 bg-red-600 rounded-full">
                      <Trash2 className="w-2.5 h-2.5 text-white" />
                    </button>
                  </div>
                ) : null}
                <label className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white text-xs cursor-pointer flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Choisir
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append('file', file);
                    try {
                      const res = await axios.post(`${API}/api/admin/site-settings/upload-image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
                      update('ad_left_image', res.data.url);
                    } catch { alert('Erreur upload'); }
                  }} />
                </label>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-gray-300">Sidebar droit</h3>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Titre de l'annonce</label>
              <input type="text" value={settings.ad_right_title || ''} onChange={e => update('ad_right_title', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none" placeholder="Ex: Partenaire officiel" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Lien (URL)</label>
              <input type="text" value={settings.ad_right_link || ''} onChange={e => update('ad_right_link', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none" placeholder="https://..." />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Code HTML / Script (optionnel)</label>
              <textarea value={settings.ad_right_html || ''} onChange={e => update('ad_right_html', e.target.value)}
                rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none font-mono" placeholder="<script>...</script> ou <iframe>...</iframe>" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Image publicitaire</label>
              <div className="flex items-center gap-3">
                {settings.ad_right_image ? (
                  <div className="relative w-32 h-20 rounded-lg overflow-hidden border border-white/10">
                    <img src={`${API}${settings.ad_right_image}`} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => update('ad_right_image', '')} className="absolute top-1 right-1 p-0.5 bg-red-600 rounded-full">
                      <Trash2 className="w-2.5 h-2.5 text-white" />
                    </button>
                  </div>
                ) : null}
                <label className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white text-xs cursor-pointer flex items-center gap-1">
                  <Upload className="w-3 h-3" /> Choisir
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append('file', file);
                    try {
                      const res = await axios.post(`${API}/api/admin/site-settings/upload-image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
                      update('ad_right_image', res.data.url);
                    } catch { alert('Erreur upload'); }
                  }} />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Google & SEO ─────────────────────────────────────────────── */}
      {/* Phase 4 — No API key required; all fields are free-text/URL. */}
      <div className="bg-white/[.03] border border-white/10 rounded-2xl p-5 space-y-4" data-testid="google-seo-section">
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          Google & Avis
        </h2>
        <p className="text-xs text-gray-500 leading-relaxed">
          Personnalise le bloc « Avis Google » affiché sur la page d'accueil.
          Aucune clé API Google n'est nécessaire — ce sont simplement des URL publiques.
          Pour trouver ton URL de fiche, cherche « Resurrection Mobile » sur Google, puis
          clique sur « Partager » → « Copier le lien » dans ta fiche Google Business.
        </p>
        <div className="grid grid-cols-1 gap-3">
          <Field
            label="URL fiche Google Business (obligatoire pour afficher le bloc)"
            field="google_business_url"
            type="url"
          />
          <Field
            label="URL pour laisser un avis (Ex: https://search.google.com/local/writereview?placeid=...)"
            field="google_review_url"
            type="url"
          />
          <Field
            label="Texte à afficher (ex: « 4.8/5 ⭐ — 320 avis Google »). Laisse vide pour un texte générique."
            field="google_rating_display"
            type="text"
          />
          <Field
            label="URL iframe Google Maps (optionnel — sinon calculé automatiquement depuis l'adresse)"
            field="google_maps_embed_url"
            type="url"
          />
        </div>
        <div className="text-[11px] text-gray-500 bg-black/40 border border-white/5 rounded-lg p-3">
          <div className="font-semibold text-gray-400 mb-1">💡 Astuce iframe Maps :</div>
          Si tu laisses <code className="text-amber-400">google_maps_embed_url</code> vide, le code génère automatiquement :
          <div className="mt-1 font-mono text-[10px] text-gray-500 break-all">
            https://maps.google.com/maps?q=&lt;adresse&gt;&amp;output=embed
          </div>
          qui fonctionne sans clé API. Pour un rendu plus joli, colle ici une URL Embed API officielle
          (nécessite clé Google Maps) si tu en as une.
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default AdminSiteSettingsPage;
